// SECURITY: Sanitize CSV values to prevent formula injection
// Excel/LibreOffice interpret cells starting with =, +, -, @, \t, \r as formulas
function sanitizeCSVValue(value) {
  if (value === null || value === undefined) {
    return ''
  }

  const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value)

  // Check if value starts with dangerous characters
  const dangerousChars = ['=', '+', '-', '@', '\t', '\r', '\n']
  const firstChar = stringValue.charAt(0)

  let sanitized = stringValue
  if (dangerousChars.includes(firstChar)) {
    // Prefix with single quote to prevent formula interpretation
    sanitized = "'" + stringValue
  }

  // Escape double quotes
  sanitized = sanitized.replace(/"/g, '""')

  return sanitized
}

export function exportToCSV(data, filename) {
  if (!data || data.length === 0) {
    throw new Error('No data to export')
  }

  const headers = Object.keys(data[0])
  const csvRows = []

  csvRows.push(headers.join(','))

  for (const row of data) {
    const values = headers.map(header => {
      const sanitizedValue = sanitizeCSVValue(row[header])
      return `"${sanitizedValue}"`
    })
    csvRows.push(values.join(','))
  }

  const csvContent = csvRows.join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })

  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)

  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  URL.revokeObjectURL(url)
}

export async function exportProjectsToCSV(supabase) {
  const { data, error } = await supabase
    .from('projects')
    .select(`
      id,
      title,
      location,
      status,
      price_range_min,
      price_range_max,
      expected_completion,
      created_at,
      developer:profiles!developer_id(full_name, company_name)
    `)
    .order('created_at', { ascending: false })

  if (error) throw error

  const exportData = data.map(project => ({
    id: project.id,
    title: project.name,
    location: project.location,
    status: project.status,
    price_min: project.price_range_min || '',
    price_max: project.price_range_max || '',
    expected_completion: project.expected_completion || '',
    developer_name: project.developer?.full_name || project.developer?.company_name || '',
    created_at: project.created_at,
  }))

  exportToCSV(exportData, `projects_${new Date().toISOString().split('T')[0]}.csv`)
}

export async function exportReservationsToCSV(supabase) {
  const { data, error } = await supabase
    .from('reservations')
    .select(`
      id,
      status,
      notes,
      created_at,
      expires_at,
      buyer:profiles!buyer_id(full_name, email),
      unit:units(
        unit_number,
        bedrooms,
        price,
        project:projects(title, location)
      )
    `)
    .order('created_at', { ascending: false })

  if (error) throw error

  const exportData = data.map(reservation => ({
    id: reservation.id,
    status: reservation.status,
    buyer_name: reservation.buyer?.full_name || '',
    buyer_email: reservation.buyer?.email || '',
    project_title: reservation.unit?.project?.title || '',
    project_location: reservation.unit?.project?.location || '',
    unit_number: reservation.unit?.unit_number || '',
    bedrooms: reservation.unit?.bedrooms || '',
    price: reservation.unit?.price || '',
    notes: reservation.notes || '',
    created_at: reservation.created_at,
    expires_at: reservation.expires_at,
  }))

  exportToCSV(exportData, `reservations_${new Date().toISOString().split('T')[0]}.csv`)
}

export async function exportAnalyticsToCSV(supabase, startDate, endDate) {
  const { data, error } = await supabase
    .from('analytics_events')
    .select('*')
    .gte('created_at', startDate)
    .lte('created_at', endDate)
    .order('created_at', { ascending: false })

  if (error) throw error

  const exportData = data.map(event => ({
    id: event.id,
    event_type: event.event_type,
    user_id: event.user_id || '',
    project_id: event.project_id || '',
    unit_id: event.unit_id || '',
    metadata: JSON.stringify(event.metadata || {}),
    created_at: event.created_at,
  }))

  exportToCSV(exportData, `analytics_${new Date().toISOString().split('T')[0]}.csv`)
}
